import React, { useState, useRef, useEffect } from "react";
import {
  MessageCircle,
  Send,
  Bot,
  User,
  ThumbsUp,
  ThumbsDown,
  Lightbulb,
  Heart,
  DollarSign,
  FileText,
  HelpCircle,
  Sparkles,
} from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { useTheme } from "../contexts/ThemeContext";

const InsuranceAssistant = ({ userPets = [] }) => {
  const { themeClasses } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const messagesEndRef = useRef(null);

  // Quick questions for users to start with
  const quickQuestions = [
    "What does pet insurance typically cover?",
    "How much does pet insurance cost?",
    "How do I file an insurance claim?",
    "Does insurance cover my pet's breed?",
    "What are pre-existing conditions?",
    "When does coverage start?",
  ];

  // Sample conversation to show capabilities
  const sampleConversation = {
    id: "sample",
    question: "Does pet insurance cover emergency visits?",
    answer: `Yes! Pet insurance typically covers emergency visits, which is often one of the most valuable benefits. Here's what's usually included:

**Emergency Coverage:**
• After-hours veterinary visits
• Emergency surgery
• Accident treatment (cuts, broken bones)
• Sudden illness care
• Diagnostic tests during emergencies

**Important Notes:**
• Accident coverage often starts immediately
• Illness coverage may have 14-30 day waiting periods
• Keep 5,000-10,000 NOK emergency fund for upfront costs

Quick action saves lives and money - don't delay emergency care due to cost concerns!`,
    category: "emergency",
    confidence: 0.92,
    timestamp: new Date().toISOString(),
    helpful: null,
  };

  useEffect(() => {
    // Add sample conversation when component mounts
    if (conversations.length === 0) {
      setConversations([sampleConversation]);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [conversations]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const askQuestion = async () => {
    if (!question.trim() || loading) return;

    const userQuestion = question.trim();
    setQuestion("");
    setLoading(true);

    // Add user question to conversation
    const userMessage = {
      id: Date.now() + "_user",
      question: userQuestion,
      isUser: true,
      timestamp: new Date().toISOString(),
    };

    setConversations((prev) => [...prev, userMessage]);

    try {
      const askInsuranceAssistant = httpsCallable(
        functions,
        "askInsuranceAssistant"
      );

      // Prepare pet details for context
      const petDetails = selectedPet
        ? {
            species: selectedPet.species,
            breed: selectedPet.breed,
            age: selectedPet.age,
          }
        : null;

      const response = await askInsuranceAssistant({
        question: userQuestion,
        petDetails,
        language: "en",
      });

      if (response.data.success) {
        const aiMessage = {
          id: response.data.conversationId,
          question: userQuestion,
          answer: response.data.answer,
          category: response.data.category,
          confidence: response.data.confidence,
          relatedTopics: response.data.relatedTopics,
          followUpSuggestions: response.data.followUpSuggestions,
          timestamp: new Date().toISOString(),
          helpful: null,
        };

        setConversations((prev) => [...prev, aiMessage]);
      }
    } catch (error) {
      console.error("Error asking insurance assistant:", error);

      const errorMessage = {
        id: Date.now() + "_error",
        question: userQuestion,
        answer:
          "I'm sorry, I'm having trouble right now. Please try again later or contact our support team for help with your insurance questions.",
        category: "error",
        confidence: 0,
        timestamp: new Date().toISOString(),
        helpful: null,
        isError: true,
      };

      setConversations((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const rateResponse = async (conversationId, helpful, feedback = "") => {
    try {
      const rateInsuranceResponse = httpsCallable(
        functions,
        "rateInsuranceResponse"
      );

      await rateInsuranceResponse({
        conversationId,
        helpful,
        feedback,
      });

      // Update local state
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId
            ? { ...conv, helpful, userFeedback: feedback }
            : conv
        )
      );
    } catch (error) {
      console.error("Error rating response:", error);
    }
  };

  const handleQuickQuestion = (questionText) => {
    setQuestion(questionText);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      askQuestion();
    }
  };

  const formatAnswer = (answer) => {
    // Convert markdown-style formatting to JSX
    return answer.split("\n").map((line, index) => {
      if (line.startsWith("**") && line.endsWith("**")) {
        return (
          <div key={index} className="font-semibold mt-3 mb-1">
            {line.slice(2, -2)}
          </div>
        );
      }
      if (line.startsWith("• ")) {
        return (
          <div key={index} className="ml-4 mb-1">
            • {line.slice(2)}
          </div>
        );
      }
      if (line.trim() === "") {
        return <div key={index} className="mb-2"></div>;
      }
      return (
        <div key={index} className="mb-1">
          {line}
        </div>
      );
    });
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return "text-green-500";
    if (confidence >= 0.6) return "text-yellow-500";
    return "text-red-500";
  };

  const getCategoryIcon = (category) => {
    const icons = {
      coverage: <Heart className="w-4 h-4" />,
      cost: <DollarSign className="w-4 h-4" />,
      claims: <FileText className="w-4 h-4" />,
      emergency: <Sparkles className="w-4 h-4" />,
      general: <HelpCircle className="w-4 h-4" />,
    };
    return icons[category] || <MessageCircle className="w-4 h-4" />;
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsOpen(true)}
          className={`${themeClasses.bg.button} text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 group`}
        >
          <MessageCircle className="w-6 h-6 group-hover:scale-110 transition-transform" />
          <div className="absolute -top-2 -left-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
            AI
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div
        className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} shadow-xl w-96 h-[600px] flex flex-col`}
      >
        {/* Header */}
        <div
          className={`${themeClasses.bg.button} text-white p-4 rounded-t-lg flex items-center justify-between`}
        >
          <div className="flex items-center space-x-3">
            <Bot className="w-6 h-6" />
            <div>
              <h3 className="font-semibold">Insurance Assistant</h3>
              <p className="text-sm opacity-80">Ask me about pet insurance</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-white hover:bg-white/20 p-1 rounded"
          >
            ✕
          </button>
        </div>

        {/* Pet Selection */}
        {userPets.length > 0 && (
          <div className="p-3 border-b border-gray-200">
            <select
              value={selectedPet?.id || ""}
              onChange={(e) => {
                const pet = userPets.find((p) => p.id === e.target.value);
                setSelectedPet(pet || null);
              }}
              className={`w-full px-3 py-2 rounded text-sm ${themeClasses.bg.input} border ${themeClasses.border.primary} ${themeClasses.text.primary}`}
            >
              <option value="">General insurance questions</option>
              {userPets.map((pet) => (
                <option key={pet.id} value={pet.id}>
                  Ask about {pet.name} ({pet.breed})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Quick Questions */}
        {conversations.length <= 1 && (
          <div className="p-4 border-b border-gray-200">
            <p
              className={`text-sm font-medium ${themeClasses.text.primary} mb-2`}
            >
              <Lightbulb className="w-4 h-4 inline mr-1" />
              Quick questions to get started:
            </p>
            <div className="space-y-2">
              {quickQuestions.slice(0, 3).map((q, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickQuestion(q)}
                  className={`text-left text-sm p-2 rounded ${themeClasses.bg.secondary} ${themeClasses.text.primary} hover:opacity-80 transition-opacity w-full`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {conversations.map((conv) => (
            <div key={conv.id}>
              {/* User Question */}
              {conv.isUser ? (
                <div className="flex justify-end">
                  <div
                    className={`${themeClasses.bg.button} text-white p-3 rounded-lg max-w-[80%] flex items-start space-x-2`}
                  >
                    <User className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">{conv.question}</div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* AI Answer */}
                  <div className="flex justify-start">
                    <div
                      className={`${themeClasses.bg.secondary} p-3 rounded-lg max-w-[90%] flex items-start space-x-2`}
                    >
                      <Bot className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500" />
                      <div className="flex-1">
                        <div
                          className={`text-sm ${themeClasses.text.primary} leading-relaxed`}
                        >
                          {formatAnswer(conv.answer)}
                        </div>

                        {/* Confidence and Category */}
                        <div className="flex items-center space-x-4 mt-2 pt-2 border-t border-gray-200">
                          <div className="flex items-center space-x-1">
                            {getCategoryIcon(conv.category)}
                            <span
                              className={`text-xs ${themeClasses.text.secondary}`}
                            >
                              {conv.category}
                            </span>
                          </div>

                          {conv.confidence > 0 && (
                            <div
                              className={`text-xs ${getConfidenceColor(
                                conv.confidence
                              )}`}
                            >
                              {Math.round(conv.confidence * 100)}% confident
                            </div>
                          )}
                        </div>

                        {/* Rating Buttons */}
                        {!conv.isError && conv.helpful === null && (
                          <div className="flex items-center space-x-2 mt-3">
                            <span
                              className={`text-xs ${themeClasses.text.secondary}`}
                            >
                              Was this helpful?
                            </span>
                            <button
                              onClick={() => rateResponse(conv.id, true)}
                              className="text-green-500 hover:bg-green-50 p-1 rounded"
                            >
                              <ThumbsUp className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => rateResponse(conv.id, false)}
                              className="text-red-500 hover:bg-red-50 p-1 rounded"
                            >
                              <ThumbsDown className="w-3 h-3" />
                            </button>
                          </div>
                        )}

                        {/* Rating Feedback */}
                        {conv.helpful !== null && (
                          <div
                            className={`text-xs mt-2 ${
                              conv.helpful ? "text-green-600" : "text-red-600"
                            }`}
                          >
                            {conv.helpful
                              ? "👍 Marked as helpful"
                              : "👎 Marked as not helpful"}
                          </div>
                        )}

                        {/* Follow-up Suggestions */}
                        {conv.followUpSuggestions &&
                          conv.followUpSuggestions.length > 0 && (
                            <div className="mt-3">
                              <p
                                className={`text-xs font-medium ${themeClasses.text.secondary} mb-2`}
                              >
                                Related questions:
                              </p>
                              <div className="space-y-1">
                                {conv.followUpSuggestions
                                  .slice(0, 2)
                                  .map((suggestion, index) => (
                                    <button
                                      key={index}
                                      onClick={() =>
                                        handleQuickQuestion(suggestion)
                                      }
                                      className={`text-xs ${themeClasses.text.primary} hover:underline block text-left`}
                                    >
                                      • {suggestion}
                                    </button>
                                  ))}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Loading Message */}
          {loading && (
            <div className="flex justify-start">
              <div
                className={`${themeClasses.bg.secondary} p-3 rounded-lg flex items-center space-x-2`}
              >
                <Bot className="w-4 h-4 text-blue-500" />
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                  <div
                    className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex space-x-2">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                selectedPet
                  ? `Ask about ${selectedPet.name}'s insurance...`
                  : "Ask about pet insurance..."
              }
              className={`flex-1 px-3 py-2 rounded-lg ${themeClasses.bg.input} border ${themeClasses.border.primary} ${themeClasses.text.primary} text-sm`}
              disabled={loading}
            />
            <button
              onClick={askQuestion}
              disabled={loading || !question.trim()}
              className={`${themeClasses.bg.button} text-white p-2 rounded-lg disabled:opacity-50 transition-all duration-200`}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>

          <p className={`text-xs ${themeClasses.text.secondary} mt-2`}>
            💡 Ask about coverage, costs, claims, or eligibility
          </p>
        </div>
      </div>
    </div>
  );
};

export default InsuranceAssistant;
